import React, { useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Polyline, Tooltip, CircleMarker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import AIRPORT_COORDS from '../lib/airportCoords';

// ---------------------------- Great-circle helper -----------------------------
// Computes intermediate points along the great-circle path between two
// lat/lng coords so the route line follows the curved path planes actually
// fly (vs a straight Mercator line that looks wrong over long distances).
function greatCirclePoints(start, end, n = 64) {
  const toRad = (d) => (d * Math.PI) / 180;
  const toDeg = (r) => (r * 180) / Math.PI;
  const [lat1, lon1] = [toRad(start[0]), toRad(start[1])];
  const [lat2, lon2] = [toRad(end[0]), toRad(end[1])];
  const d = 2 * Math.asin(
    Math.sqrt(
      Math.sin((lat2 - lat1) / 2) ** 2 +
      Math.cos(lat1) * Math.cos(lat2) * Math.sin((lon2 - lon1) / 2) ** 2,
    ),
  );
  if (d === 0) return [start, end];
  const points = [];
  for (let i = 0; i <= n; i++) {
    const f = i / n;
    const a = Math.sin((1 - f) * d) / Math.sin(d);
    const b = Math.sin(f * d) / Math.sin(d);
    const x = a * Math.cos(lat1) * Math.cos(lon1) + b * Math.cos(lat2) * Math.cos(lon2);
    const y = a * Math.cos(lat1) * Math.sin(lon1) + b * Math.cos(lat2) * Math.sin(lon2);
    const z = a * Math.sin(lat1) + b * Math.sin(lat2);
    const lat = Math.atan2(z, Math.sqrt(x * x + y * y));
    const lon = Math.atan2(y, x);
    points.push([toDeg(lat), toDeg(lon)]);
  }
  return points;
}

// Plane icon — rotates to match the flight direction
function planeIcon(rotation = 0) {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24"
         style="transform: rotate(${rotation}deg); filter: drop-shadow(0 2px 4px rgba(0,0,0,0.4));">
      <path fill="#10b981" stroke="#fff" stroke-width="0.8"
        d="M21,16V14L13,9V3.5A1.5,1.5 0 0,0 11.5,2A1.5,1.5 0 0,0 10,3.5V9L2,14V16L10,13.5V19L8,20.5V22L11.5,21L15,22V20.5L13,19V13.5L21,16Z" />
    </svg>`;
  return L.divIcon({
    html: svg,
    className: 'flight-plane-marker',
    iconSize: [36, 36],
    iconAnchor: [18, 18],
  });
}

const cityBox = (a, b) => {
  // Bounds covering both endpoints with a little padding
  const lats = [a[0], b[0]];
  const lngs = [a[1], b[1]];
  return [
    [Math.min(...lats) - 2, Math.min(...lngs) - 4],
    [Math.max(...lats) + 2, Math.max(...lngs) + 4],
  ];
};

export const FlightMap = ({ depIata, arrIata, lat, lng, direction = 0, isActive = false }) => {
  const dep = depIata ? AIRPORT_COORDS[depIata.toUpperCase()] : null;
  const arr = arrIata ? AIRPORT_COORDS[arrIata.toUpperCase()] : null;

  const route = useMemo(() => (dep && arr ? greatCirclePoints(dep, arr, 80) : []), [dep, arr]);
  const bounds = useMemo(() => (dep && arr ? cityBox(dep, arr) : null), [dep, arr]);

  if (!dep || !arr) {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 text-center text-sm text-slate-500">
        Live map unavailable — airport coordinates not known.
      </div>
    );
  }

  const planePos = isActive && Number.isFinite(lat) && Number.isFinite(lng) ? [lat, lng] : null;

  return (
    <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm" data-testid="flight-map">
      <MapContainer
        bounds={bounds}
        style={{ height: 340, width: '100%' }}
        scrollWheelZoom={false}
        attributionControl={false}
      >
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          subdomains={['a', 'b', 'c', 'd']}
        />
        {/* Departure marker */}
        <CircleMarker center={dep} radius={6} color="#0f766e" fillColor="#10b981" fillOpacity={0.9} weight={2}>
          <Tooltip permanent direction="top" offset={[0, -8]} className="!bg-white !border !border-slate-200 !shadow !text-xs !font-bold !text-slate-800">
            {depIata}
          </Tooltip>
        </CircleMarker>
        {/* Arrival marker */}
        <CircleMarker center={arr} radius={6} color="#0f766e" fillColor="#10b981" fillOpacity={0.9} weight={2}>
          <Tooltip permanent direction="top" offset={[0, -8]} className="!bg-white !border !border-slate-200 !shadow !text-xs !font-bold !text-slate-800">
            {arrIata}
          </Tooltip>
        </CircleMarker>
        {/* Great circle route */}
        <Polyline
          positions={route}
          pathOptions={{
            color: planePos ? '#10b981' : '#94a3b8',
            weight: 3,
            opacity: planePos ? 0.85 : 0.6,
            dashArray: planePos ? null : '6 8',
          }}
        />
        {/* Live plane position */}
        {planePos && (
          <Marker position={planePos} icon={planeIcon(direction)}>
            <Tooltip direction="top" offset={[0, -12]} className="!bg-emerald-700 !text-white !border-0 !text-xs !font-semibold">
              Currently here · {planePos[0].toFixed(2)}°, {planePos[1].toFixed(2)}°
            </Tooltip>
          </Marker>
        )}
      </MapContainer>
      {!planePos && (
        <div className="bg-slate-50 px-4 py-2 text-[11px] text-slate-500 text-center border-t border-slate-100">
          {isActive
            ? 'Flight is airborne but live position not yet available — refreshing shortly…'
            : 'Live aircraft tracking shows once the flight is in the air.'}
        </div>
      )}
    </div>
  );
};

export default FlightMap;
